import React, { useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Badge } from '@mui/material';
import { DASHBOARD_ROUTES } from '../../Routes';
import IconBuilding from '../../assets/icon-components/building';
import IconContact from '../../assets/icon-components/contact';
import IconInbox from '../../assets/icon-components/inbox';
import IconMail from '../../assets/icon-components/mail';
import IconUser from '../../assets/icon-components/user';
import logoCompact from '../../assets/temp/logo_compact.svg';
import { getInboxCounts } from '../../data/api/conversation.api';
import { saveInboxCounts, selectInboxCounts } from '../../store/inboxSlice';
import { selectAccount } from '../../store/accountSlide';
import './Sidebar.scss';

export const Sidebar: React.FC = () => {
  const dispatch = useDispatch();
  const counts = useSelector(selectInboxCounts);
  // Server-owned: GET /users answers whether this user gets an inbox at all.
  // Everyone else must not see the entry, because the endpoints behind it
  // refuse anyone outside the allowlist and a nav item that 403s is worse
  // than no nav item.
  const account = useSelector(selectAccount);
  const canUseInbox = !!account?.canUseInbox;
  const isPlatformAdmin = !!account?.isPlatformAdmin;

  useEffect(() => {
    if (!canUseInbox) return;
    // Seed the badge on load; the inbox page keeps it fresh afterwards.
    // Silent on failure — a missing badge is not worth a toast.
    const requestedAt = Date.now();
    getInboxCounts()
      .then((counts) => dispatch(saveInboxCounts({ counts, requestedAt })))
      .catch(() => undefined);
  }, [dispatch, canUseInbox]);

  const activateClassLink = ({ isActive }: { isActive: boolean }) => {
    if (isActive) {
      return 'text-[#f6b26b]';
    } else {
      return 'text-white';
    }
  };

  return (
    <div
      id="menu"
      className="fixed left-0 top-0 w-[64px] h-full bg-[#2c3f54] text-center pt-[10px] box-border"
    >
      <Link to={DASHBOARD_ROUTES.ROUTE_COMPANY}>
        <img width="24px" src={logoCompact} alt="logoCompact" />
      </Link>

      <div className="pt-[60px]">
        <div className="mb-[40px]">
          <NavLink
            to={DASHBOARD_ROUTES.ROUTE_COMPANY}
            className={activateClassLink}
          >
            <IconBuilding className="cursor-pointer w-[24px]" />
          </NavLink>
        </div>
        <div className="mb-[40px]">
          <NavLink
            to={DASHBOARD_ROUTES.ROUTE_USER}
            className={activateClassLink}
          >
            <IconUser className="cursor-pointer w-[24px]" />
          </NavLink>
        </div>

        <div className="mb-[40px]">
          <NavLink
            to={DASHBOARD_ROUTES.ROUTE_PROVIDER}
            className={activateClassLink}
          >
            <IconContact className="cursor-pointer w-[24px]" />
          </NavLink>
        </div>

        {canUseInbox && (
          <div className="mb-[40px]">
            <NavLink
              to={DASHBOARD_ROUTES.ROUTE_INBOX}
              className={activateClassLink}
            >
              <Badge
                badgeContent={counts?.unassigned || 0}
                max={99}
                color="error"
                overlap="circular"
                title="Unassigned conversations"
              >
                <IconInbox className="cursor-pointer w-[24px]" />
              </Badge>
            </NavLink>
          </div>
        )}

        {isPlatformAdmin && (
          <div className="mb-[40px]">
            <NavLink
              to={DASHBOARD_ROUTES.ROUTE_SUPPORT_INBOX}
              className={activateClassLink}
              title="Support inbox connection"
              aria-label="Support inbox connection"
            >
              <IconMail className="cursor-pointer w-[24px]" />
            </NavLink>
          </div>
        )}
      </div>
    </div>
  );
};
